import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
    AlertCircle,
    CheckCircle2,
    FileImage,
    FileQuestion,
    FileVideo,
    Loader2,
    Pause,
    Play,
    RotateCcw,
    SkipForward,
    Sparkles,
    Trash2,
    Upload,
    UploadCloud,
    X,
} from 'lucide-react';
import { toast } from 'sonner';
import useBulkUploader, {
    UploadFileItem,
    UploadFileStatus,
} from './hooks/useBulkUploader';
import useGalleryCloudProcessing from './hooks/useGalleryCloudProcessing';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/src/shadComponents/ui/dialog';
import { Button } from '@/src/shadComponents/ui/button';
import { Progress } from '@/src/shadComponents/ui/progress';
import { Switch } from '@/src/shadComponents/ui/switch';
import { cn } from '@/src/lib/utils';

interface BulkUploadMediaProps {
    activationId: number;
    photoSessionId?: string;
    galleryId?: number | null;
    onCancel: () => void;
    onComplete: () => void;
}

const MAX_FILE_SIZE_BYTES = 500 * 1024 * 1024; // 500 MB safety cap

const STATUS_COPY: Record<UploadFileStatus, string> = {
    pending: 'Pending',
    uploading: 'Uploading',
    success: 'Uploaded',
    error: 'Failed',
    skipped: 'Skipped',
    cancelled: 'Cancelled',
};

function formatBytes(bytes: number): string {
    if (!bytes || bytes < 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let value = bytes;
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) {
        value /= 1024;
        unit += 1;
    }
    const decimals = value < 10 && unit > 0 ? 1 : 0;
    return `${value.toFixed(decimals)} ${units[unit]}`;
}

function FileIconForType({ file }: { file: File }) {
    if (file.type.startsWith('image/')) {
        return <FileImage className="h-5 w-5 text-gray-500" />;
    }
    if (file.type.startsWith('video/')) {
        return <FileVideo className="h-5 w-5 text-gray-500" />;
    }
    return <FileQuestion className="h-5 w-5 text-gray-500" />;
}

function StatusPill({ status }: { status: UploadFileStatus }) {
    const baseClasses =
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium';

    switch (status) {
        case 'pending':
            return (
                <span className={cn(baseClasses, 'bg-gray-100 text-gray-700')}>
                    {STATUS_COPY.pending}
                </span>
            );
        case 'uploading':
            return (
                <span className={cn(baseClasses, 'bg-blue-50 text-blue-700')}>
                    <Loader2 className="h-3 w-3 animate-spin" />
                    {STATUS_COPY.uploading}
                </span>
            );
        case 'success':
            return (
                <span
                    className={cn(baseClasses, 'bg-green-50 text-green-700')}
                >
                    <CheckCircle2 className="h-3 w-3" />
                    {STATUS_COPY.success}
                </span>
            );
        case 'error':
            return (
                <span className={cn(baseClasses, 'bg-red-50 text-red-700')}>
                    <AlertCircle className="h-3 w-3" />
                    {STATUS_COPY.error}
                </span>
            );
        case 'skipped':
            return (
                <span
                    className={cn(baseClasses, 'bg-amber-50 text-amber-700')}
                >
                    <SkipForward className="h-3 w-3" />
                    {STATUS_COPY.skipped}
                </span>
            );
        case 'cancelled':
            return (
                <span className={cn(baseClasses, 'bg-red-50 text-red-700')}>
                    <X className="h-3 w-3" />
                    {STATUS_COPY.cancelled}
                </span>
            );
        default:
            return null;
    }
}

interface FileRowProps {
    item: UploadFileItem;
    onRemove: (id: string) => void;
    onSkip: (id: string) => void;
    onRetry: (id: string) => void;
    requestPreview: (id: string) => string | undefined;
}

function FileRow({ item, onRemove, onSkip, onRetry, requestPreview }: FileRowProps) {
    // Resolve the preview URL lazily when this row actually renders. The hook
    // caches the URL by file id, so repeated calls are cheap and the URL
    // stays stable across scroll-driven re-mounts inside the virtualizer.
    const previewUrl = item.isPreviewable ? requestPreview(item.id) : undefined;

    const showProgressBar =
        item.status === 'uploading' ||
        (item.status === 'success' && item.progress > 0) ||
        (item.status === 'error' && item.progress > 0);

    const progressColor =
        item.status === 'success'
            ? 'bg-green-500'
            : item.status === 'error' || item.status === 'cancelled'
            ? 'bg-red-400'
            : 'bg-blue-500';

    const isDimmed = item.status === 'skipped' || item.status === 'cancelled';

    const canSkip = item.status === 'pending' || item.status === 'uploading';
    const canRetry =
        item.status === 'error' ||
        item.status === 'cancelled' ||
        item.status === 'skipped';
    const canRemove = item.status !== 'uploading';

    return (
        <div
            className={cn(
                'group flex items-center gap-3 rounded-md border border-gray-200 bg-white p-2 pr-3 transition',
                isDimmed && 'opacity-60',
                item.status === 'error' && 'border-red-200 bg-red-50/30',
                item.status === 'success' && 'border-green-200',
            )}
        >
            <div className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-md bg-gray-100">
                {previewUrl ? (
                    <img
                        src={previewUrl}
                        alt={item.file.name}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover"
                    />
                ) : (
                    <div className="flex h-full w-full items-center justify-center">
                        <FileIconForType file={item.file} />
                    </div>
                )}
                {item.status === 'success' && (
                    <div className="absolute inset-0 flex items-center justify-center bg-green-500/85">
                        <CheckCircle2 className="h-6 w-6 text-white" />
                    </div>
                )}
                {item.status === 'error' && (
                    <div className="absolute inset-0 flex items-center justify-center bg-red-500/85">
                        <AlertCircle className="h-6 w-6 text-white" />
                    </div>
                )}
                {item.status === 'skipped' && (
                    <div className="absolute inset-0 flex items-center justify-center bg-amber-500/85">
                        <SkipForward className="h-6 w-6 text-white" />
                    </div>
                )}
            </div>

            <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                        <p
                            className={cn(
                                'truncate text-sm font-medium text-gray-900',
                                isDimmed && 'line-through',
                            )}
                            title={item.file.name}
                        >
                            {item.file.name}
                        </p>
                        <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-500">
                            <span>{formatBytes(item.file.size)}</span>
                            {item.attempts > 1 && (
                                <span className="text-gray-400">
                                    Attempt {item.attempts}
                                </span>
                            )}
                        </div>
                    </div>
                    <StatusPill status={item.status} />
                </div>

                {showProgressBar && (
                    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                        <div
                            className={cn(
                                'h-full transition-all duration-150',
                                progressColor,
                            )}
                            style={{ width: `${item.progress}%` }}
                        />
                    </div>
                )}

                {item.status === 'error' && item.error && (
                    <p className="mt-1 truncate text-xs text-red-600" title={item.error}>
                        {item.error}
                    </p>
                )}
            </div>

            <div className="flex flex-shrink-0 items-center gap-1">
                {canRetry && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => onRetry(item.id)}
                        title="Retry"
                    >
                        <RotateCcw className="h-4 w-4" />
                    </Button>
                )}
                {canSkip && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => onSkip(item.id)}
                        title="Skip"
                    >
                        <SkipForward className="h-4 w-4" />
                    </Button>
                )}
                {canRemove && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-gray-500 hover:text-red-600"
                        onClick={() => onRemove(item.id)}
                        title="Remove"
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                )}
            </div>
        </div>
    );
}

export default function BulkUploadMedia({
    activationId,
    photoSessionId,
    galleryId,
    onCancel,
    onComplete,
}: BulkUploadMediaProps) {
    const cloudProcessing = useGalleryCloudProcessing(galleryId);

    // Default ON when a cloud processing flow is available so users get the
    // configured processing applied automatically; user can opt out per-session.
    const [runProcessingFlow, setRunProcessingFlow] = useState(false);
    useEffect(() => {
        if (cloudProcessing.isAvailable) {
            setRunProcessingFlow(true);
        } else {
            setRunProcessingFlow(false);
        }
    }, [cloudProcessing.isAvailable]);

    const {
        files,
        summary,
        isUploading,
        isPaused,
        hasFinished,
        addFiles,
        removeFile,
        skipFile,
        retryFile,
        retryAllFailed,
        startUpload,
        pause,
        resume,
        cancelAll,
        clearAll,
        requestPreview,
    } = useBulkUploader({
        activationId,
        photoSessionId,
        maxFileSizeBytes: MAX_FILE_SIZE_BYTES,
        runProcessingFlow,
    });

    const scrollParentRef = useRef<HTMLDivElement | null>(null);

    // Virtualize the list of files. Each row has slightly variable height
    // (depending on whether it shows a progress bar or an error line), so we
    // use dynamic measurement via `measureElement`. With this in place, only
    // the rows currently visible (+overscan) are mounted, which keeps the
    // dialog responsive when users select thousands of files.
    const rowVirtualizer = useVirtualizer({
        count: files.length,
        getScrollElement: () => scrollParentRef.current,
        // ~ row height (~64px content + 8px gap). Real heights are measured
        // after mount; this is just the initial estimate.
        estimateSize: () => 72,
        overscan: 8,
        getItemKey: (index) => files[index]?.id ?? index,
    });

    const virtualItems = rowVirtualizer.getVirtualItems();

    const handleDrop = useCallback(
        (accepted: File[], rejections: FileRejection[]) => {
            if (rejections.length > 0) {
                const reasons = new Set<string>();
                for (const r of rejections) {
                    for (const err of r.errors) {
                        reasons.add(err.message);
                    }
                }
                toast.error(
                    `${rejections.length} file${
                        rejections.length === 1 ? '' : 's'
                    } rejected: ${Array.from(reasons).join(', ')}`,
                );
            }

            if (accepted.length === 0) return;

            const { added, rejected } = addFiles(accepted);

            if (rejected.length > 0) {
                toast.error(
                    `${rejected.length} file${
                        rejected.length === 1 ? '' : 's'
                    } skipped (too large or invalid).`,
                );
            }

            if (added > 0) {
                toast.success(`Added ${added} file${added === 1 ? '' : 's'}`);
            }
        },
        [addFiles],
    );

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        onDrop: handleDrop,
        noClick: true,
        noKeyboard: true,
        accept: {
            'image/*': [
                '.jpg',
                '.jpeg',
                '.png',
                '.gif',
                '.webp',
                '.heic',
                '.heif',
                '.avif',
            ],
            'video/*': ['.mp4', '.mov', '.webm', '.m4v'],
        },
        maxSize: MAX_FILE_SIZE_BYTES,
    });

    // When all uploads have finished and at least one succeeded, fire onComplete-style notice.
    const successfulCount = summary.success;
    const failedCount = summary.failed;
    useEffect(() => {
        if (hasFinished) {
            if (successfulCount > 0 && failedCount === 0) {
                toast.success(
                    `Upload complete - ${successfulCount} file${
                        successfulCount === 1 ? '' : 's'
                    } uploaded`,
                );
            } else if (successfulCount > 0 && failedCount > 0) {
                toast.warning(
                    `Upload finished with errors: ${successfulCount} uploaded, ${failedCount} failed`,
                );
            } else if (successfulCount === 0 && failedCount > 0) {
                toast.error(
                    `All ${failedCount} upload${
                        failedCount === 1 ? '' : 's'
                    } failed`,
                );
            }
        }
    }, [hasFinished, successfulCount, failedCount]);

    const handleClose = useCallback(() => {
        if (isUploading) {
            const ok = window.confirm(
                'Uploads are still in progress. Cancel and close?',
            );
            if (!ok) return;
            cancelAll();
        }

        // If anything succeeded during this session, notify the parent so it can refresh.
        if (summary.success > 0) {
            onComplete();
        } else {
            onCancel();
        }
        clearAll();
    }, [isUploading, cancelAll, clearAll, summary.success, onCancel, onComplete]);

    const handleDialogChange = useCallback(
        (open: boolean) => {
            if (!open) handleClose();
        },
        [handleClose],
    );

    const totalSizeLabel = useMemo(
        () => formatBytes(summary.bytesTotal),
        [summary.bytesTotal],
    );
    const uploadedSizeLabel = useMemo(
        () => formatBytes(summary.bytesUploaded),
        [summary.bytesUploaded],
    );

    const hasAnyFiles = files.length > 0;
    const hasPending = summary.pending > 0;
    const hasFailed = summary.failed > 0;
    const allDone =
        hasAnyFiles &&
        summary.pending === 0 &&
        summary.uploading === 0;

    return (
        <Dialog open onOpenChange={handleDialogChange}>
            <DialogContent
                className="flex max-h-[90vh] w-[95vw] max-w-3xl flex-col gap-0 p-0 sm:max-w-3xl"
                onPointerDownOutside={(e) => {
                    if (isUploading) e.preventDefault();
                }}
                onEscapeKeyDown={(e) => {
                    if (isUploading) e.preventDefault();
                }}
            >
                <DialogHeader className="border-b border-gray-200 p-4 sm:p-5">
                    <DialogTitle className="flex items-center gap-2">
                        <UploadCloud className="h-5 w-5 text-gray-700" />
                        Bulk Upload
                    </DialogTitle>
                    <DialogDescription>
                        {hasAnyFiles
                            ? `${summary.total} file${
                                  summary.total === 1 ? '' : 's'
                              } selected · ${totalSizeLabel}`
                            : 'Drop files here, or browse to select files to upload.'}
                    </DialogDescription>
                </DialogHeader>

                <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden p-4 sm:p-5">
                    <div
                        {...getRootProps()}
                        className={cn(
                            'relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50/60 transition-colors',
                            hasAnyFiles ? 'px-4 py-3' : 'px-6 py-10',
                            isDragActive && 'border-blue-500 bg-blue-50',
                        )}
                    >
                        <input {...getInputProps()} />
                        {isDragActive ? (
                            <p className="text-sm font-medium text-blue-700">
                                Drop files to add them
                            </p>
                        ) : hasAnyFiles ? (
                            <div className="flex w-full items-center justify-between gap-3">
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <Upload className="h-4 w-4" />
                                    <span>Drag more files or</span>
                                </div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={open}
                                >
                                    Add files
                                </Button>
                            </div>
                        ) : (
                            <>
                                <UploadCloud className="mb-3 h-10 w-10 text-gray-400" />
                                <p className="text-sm font-medium text-gray-700">
                                    Drag & drop images or videos here
                                </p>
                                <p className="mt-1 text-xs text-gray-500">
                                    Or
                                </p>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="mt-2"
                                    onClick={open}
                                >
                                    Choose files
                                </Button>
                                <p className="mt-3 text-xs text-gray-400">
                                    Up to {formatBytes(MAX_FILE_SIZE_BYTES)} per file
                                </p>
                            </>
                        )}
                    </div>

                    {cloudProcessing.isAvailable && (
                        <div className="flex items-start justify-between gap-3 rounded-md border border-blue-100 bg-blue-50/50 p-3">
                            <div className="flex items-start gap-2.5">
                                <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-blue-100">
                                    <Sparkles className="h-4 w-4 text-blue-700" />
                                </div>
                                <div className="min-w-0">
                                    <label
                                        htmlFor="bulk-upload-run-processing"
                                        className="block text-sm font-medium text-gray-900"
                                    >
                                        Run cloud processing
                                    </label>
                                    <p className="mt-0.5 text-xs text-gray-600">
                                        {runProcessingFlow
                                            ? 'Each file will be processed using this activation\u2019s configured cloud flow before being saved.'
                                            : 'Files will be saved as-is, without running the configured cloud processing flow.'}
                                    </p>
                                </div>
                            </div>
                            <Switch
                                id="bulk-upload-run-processing"
                                checked={runProcessingFlow}
                                onCheckedChange={setRunProcessingFlow}
                                disabled={isUploading}
                                aria-label="Run cloud processing on uploaded files"
                            />
                        </div>
                    )}

                    {hasAnyFiles && (
                        <div className="rounded-md border border-gray-200 bg-white p-3">
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                                    <span className="text-gray-600">
                                        <span className="font-semibold text-gray-900">
                                            {summary.success}
                                        </span>{' '}
                                        / {summary.total} uploaded
                                    </span>
                                    {summary.uploading > 0 && (
                                        <span className="text-blue-600">
                                            {summary.uploading} in progress
                                        </span>
                                    )}
                                    {summary.pending > 0 && (
                                        <span className="text-gray-500">
                                            {summary.pending} pending
                                        </span>
                                    )}
                                    {summary.failed > 0 && (
                                        <span className="text-red-600">
                                            {summary.failed} failed
                                        </span>
                                    )}
                                    {summary.skipped > 0 && (
                                        <span className="text-amber-600">
                                            {summary.skipped} skipped
                                        </span>
                                    )}
                                </div>
                                <div className="text-xs text-gray-500">
                                    {uploadedSizeLabel} / {totalSizeLabel}
                                </div>
                            </div>
                            <Progress
                                value={summary.overallProgress}
                                className="mt-2 h-2"
                            />
                        </div>
                    )}

                    {hasAnyFiles && (
                        <div
                            ref={scrollParentRef}
                            className="flex-1 min-h-[200px] overflow-y-auto rounded-md border border-gray-200 bg-gray-50/40"
                        >
                            <div
                                style={{
                                    height: rowVirtualizer.getTotalSize(),
                                    position: 'relative',
                                    width: '100%',
                                }}
                            >
                                {virtualItems.map((virtualRow) => {
                                    const file = files[virtualRow.index];
                                    if (!file) return null;
                                    return (
                                        <div
                                            key={virtualRow.key}
                                            data-index={virtualRow.index}
                                            ref={rowVirtualizer.measureElement}
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                transform: `translateY(${virtualRow.start}px)`,
                                                paddingTop: virtualRow.index === 0 ? 8 : 4,
                                                paddingBottom:
                                                    virtualRow.index === files.length - 1
                                                        ? 8
                                                        : 4,
                                                paddingLeft: 8,
                                                paddingRight: 8,
                                            }}
                                        >
                                            <FileRow
                                                item={file}
                                                onRemove={removeFile}
                                                onSkip={skipFile}
                                                onRetry={retryFile}
                                                requestPreview={requestPreview}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-between gap-2 border-t border-gray-200 p-4 sm:p-5">
                    <div className="flex items-center gap-2">
                        {hasFailed && !isUploading && (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={retryAllFailed}
                            >
                                <RotateCcw className="mr-1.5 h-4 w-4" />
                                Retry failed ({summary.failed})
                            </Button>
                        )}
                        {hasAnyFiles && !isUploading && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={clearAll}
                                className="text-gray-500"
                            >
                                Clear all
                            </Button>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        {!isUploading && !allDone && (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleClose}
                            >
                                Cancel
                            </Button>
                        )}

                        {!isUploading && hasPending && (
                            <Button
                                type="button"
                                onClick={startUpload}
                                disabled={!hasPending}
                            >
                                <Upload className="mr-1.5 h-4 w-4" />
                                Upload {summary.pending} file
                                {summary.pending === 1 ? '' : 's'}
                            </Button>
                        )}

                        {isUploading && !isPaused && (
                            <>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={pause}
                                >
                                    <Pause className="mr-1.5 h-4 w-4" />
                                    Pause
                                </Button>
                                <Button
                                    type="button"
                                    variant="destructive"
                                    onClick={() => {
                                        cancelAll();
                                    }}
                                >
                                    Cancel uploads
                                </Button>
                            </>
                        )}

                        {isUploading && isPaused && (
                            <>
                                <Button
                                    type="button"
                                    variant="destructive"
                                    onClick={() => {
                                        cancelAll();
                                    }}
                                >
                                    Cancel uploads
                                </Button>
                                <Button type="button" onClick={resume}>
                                    <Play className="mr-1.5 h-4 w-4" />
                                    Resume
                                </Button>
                            </>
                        )}

                        {!isUploading && allDone && (
                            <Button type="button" onClick={handleClose}>
                                Done
                            </Button>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
